import React, { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useHandHistorySnapshotMode } from 'contexts/HandHistorySnapshotModeContext';
import gsap from 'gsap';
import { useTableSelector } from 'hooks/useTableSelector';
import { ZIndex } from 'styles';
import { potWinningCards, potWinningHand } from 'utils/functions';

import { mapToCard } from 'components/Card';
import XSmallCard from 'components/Card/XSmallCard';

import { getRunKeys, winnerBannerRun } from './HoldEmCards';
import { parseRuns } from './Pot';

const hasWinner = (key: string) => {
  return ['GAME_MSG_ALLFOLD'].indexOf(key) > -1;
};

// EN-10766: the live table and history board show a category label; the full
// description (articles, ranks and kickers) belongs in hand-history sentences.
const handLabel = (description: string) => {
  const text = description
    .replace(/\s*\(.*?\)\s*$/, '')
    .replace(/ - \w+ kicker$/, '')
    .trim();
  const category = text.match(
    /\b(royal flush|straight flush|four of a kind|full house|three of a kind|two pair|high card|straight|flush|pair)\b/i
  );
  // Keep low-hand ranks and unfamiliar historical descriptions readable.
  return (category?.[1] ?? text.replace(/^an?\s+/i, '')).toUpperCase();
};

/**
 * Felt left between the pot and the banner when the banner rises to meet it.
 * The calibration knob for "just below the pots" — raise it for more air.
 */
const BANNER_POT_GAP = 4;

const clamp = (value: number, min: number, max: number) =>
  // Reversed bounds — a banner taller than the whole fork — collapse to the
  // fork's middle rather than snapping to one end.
  min > max ? (min + max) / 2 : Math.min(Math.max(value, min), max);

/**
 * How far to nudge the banner so it sits on the fork row it belongs to.
 *
 * MEASURED, not computed: the row's own box is the only thing that knows where
 * it painted. This was written when the fork was drawn inside the table's
 * rotateX tilt, where rows painted at different heights the further back they
 * sat (70.7 / 75.4 / 80.7px for three portrait runs). The board is flat now
 * (EN-11031), but measuring still beats row-counting arithmetic — it holds
 * through the board's scale, side pots and anything else that moves a row. The
 * banner stays outside the board column, at ONE size in every run count and on
 * every row, and simply asks the row where it is.
 *
 * Self-correcting: `shift` is the nudge already applied, so it is added back to
 * turn the banner's current position into its un-nudged one. That makes the
 * result a fixed point — a second pass computes the same number — instead of
 * drifting by the nudge on every measurement.
 *
 * The banner is TALLER than a branch row (89.7px against 71–81px), so centring
 * it on its row put the excess over the neighbouring board — the one the banner
 * is not about. It never crosses the row's INNER edge instead, and the excess
 * goes outward into felt: up off the top row, down off the bottom one. The
 * banner only ever rides the first or last row (see winnerBannerRun), so the
 * outward side is always the empty one.
 *
 * `ceiling` / `floor` bound that outward spill — the pot's underside where the
 * pot stacks above the board (portrait), else the felt's own edge. If they
 * leave less room than the banner needs, the clamp's reversed-bound fallback
 * centres it and the overlap comes back; portrait buys the room by lifting the
 * pot while a fork is on the felt (portraitPotAbove).
 */
export const bannerRowShift = (
  banner: DOMRect,
  row: DOMRect,
  isTopRow: boolean,
  ceiling: number,
  floor: number,
  shift: number
): number => {
  const half = banner.height / 2;
  const centre = row.top + row.height / 2;
  const target = isTopRow
    ? clamp(centre, ceiling + half, row.bottom - half)
    : clamp(centre, row.top + half, floor - half);
  return shift + (target - (banner.top + half));
};

/**
 * The DOM the banner is allowed to measure: its OWN table.
 *
 * `closest()`, never `document.querySelector` — the grid mounts one
 * `[data-table-root]` per cell (plus kept-alive panels), so the document's FIRST
 * `[data-run-row]` can belong to another table. The banner would then nudge
 * itself onto a fork it is not on, or find no row at all when its own table is
 * the only one forked. Same rule as ReturnedBetChips and useTrayClearanceLift.
 */
export const forkScope = (container: HTMLElement): ParentNode =>
  container.closest('[data-table-root]') ?? document;

const TableWinnerMessage = () => {
  const isHandHistorySnapshot = useHandHistorySnapshotMode();
  const { pots, showdown, languageKey, msg: tableMsg } = useTableSelector((table) => table.game);
  const isAllFoldWin = hasWinner(languageKey);
  // No cards to show on an all-fold win — the hand never reached showdown.
  const winningCards = isAllFoldWin ? [] : potWinningCards(pots, showdown);
  const rawMsg = isAllFoldWin ? tableMsg : potWinningHand(pots, showdown);
  const msg = !isAllFoldWin && typeof rawMsg === 'string' ? handLabel(rawMsg) : rawMsg;
  const bannerRef = useRef<HTMLDivElement>(null);

  // Which fork row this banner belongs to, if a fork is on the felt at all.
  const isMultiRunActive = useTableSelector((table) => table.currentPlayer.isMultiRunActive);
  const multiRunCards = useTableSelector((table) => table.game.multiRunCards);
  const runs = useTableSelector((table) => table.game.runs);
  const targetRun = isMultiRunActive
    ? winnerBannerRun(getRunKeys(multiRunCards, runs), parseRuns(runs)?.currentRun ?? 0)
    : '';

  const containerRef = useRef<HTMLDivElement>(null);
  const [shift, setShift] = useState(0);
  // Read inside the measure callback, never as a dependency: the callback is
  // what WRITES it, so depending on it would re-arm the observer on every nudge.
  const shiftRef = useRef(0);
  shiftRef.current = shift;

  // A single-board hand keeps its own anchor, so drop any nudge from a fork that
  // has left the felt.
  useLayoutEffect(() => {
    if (!targetRun) setShift(0);
  }, [targetRun]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !targetRun || !msg) return undefined;

    const scope = forkScope(container);

    const measure = () => {
      const rows = scope.querySelectorAll<HTMLElement>('[data-run-row]');
      const row = scope.querySelector<HTMLElement>(`[data-run-row="${targetRun}"]`);
      if (!row || rows.length < 2) return;
      const boxes = Array.from(rows, (el) => el.getBoundingClientRect());
      const forkTop = Math.min(...boxes.map((b) => b.top));
      // How far the banner may spill off its row. Portrait stacks the pot ABOVE
      // the board, so there the pot's underside is the real ceiling; landscape
      // parks the pot beside the board, where nothing is overhead and only the
      // felt's own edge stops it. Decided by where the pot actually IS rather
      // than by an orientation flag.
      const felt = scope instanceof Element ? scope.getBoundingClientRect() : null;
      const potBottoms = Array.from(
        scope.querySelectorAll<HTMLElement>('[data-pot-cluster]'),
        (el) => el.getBoundingClientRect().bottom
      ).filter((bottom) => bottom <= forkTop);
      const ceiling = potBottoms.length ? Math.max(...potBottoms) + BANNER_POT_GAP : felt?.top ?? 0;
      const next = bannerRowShift(
        container.getBoundingClientRect(),
        row.getBoundingClientRect(),
        rows[0] === row,
        ceiling,
        felt?.bottom ?? window.innerHeight,
        shiftRef.current
      );
      // Sub-pixel churn would re-render forever; the fixed point is exact enough.
      if (Math.abs(next - shiftRef.current) >= 0.5) setShift(next);
    };

    measure();
    // The felt resizes (window, orientation, the table's own view scale) and the
    // fork grows a row per run, both of which move the row out from under the
    // banner. Watching the two boxes covers every cause without a dependency
    // list that has to enumerate them.
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    const stack = scope.querySelector('[data-run-row]')?.parentElement;
    if (stack) observer.observe(stack);
    return () => observer.disconnect();
  }, [targetRun, msg]);

  useEffect(() => {
    const el = bannerRef.current;
    if (!el || !msg) return;
    if (isHandHistorySnapshot) {
      gsap.set(el, { opacity: 1, scale: 1, y: 0 });
      return;
    }
    gsap.fromTo(
      el,
      { opacity: 0, scale: 0.8, y: 10 },
      { opacity: 1, scale: 1, y: 0, duration: 0.4, ease: 'back.out(1.5)' }
    );
    return () => {
      gsap.killTweensOf(el);
    };
  }, [isHandHistorySnapshot, msg]);

  return (
    msg && (
      <div
        ref={containerRef}
        style={shift ? { ...containerStyle, transform: `translateY(${shift}px)` } : containerStyle}>
        <div ref={bannerRef} style={bannerStyle}>
          <div style={headerRowStyle}>
            <EmojiEventsIcon style={{ fontSize: '1.2rem', color: '#FFD700' }} />
            <span style={textStyle}>{msg}</span>
          </div>
          {winningCards.length > 0 && (
            <div style={cardsRowStyle}>
              {winningCards.map((card, i) => {
                const pokerCard = mapToCard(card);
                if (!pokerCard) return null;
                return (
                  <XSmallCard
                    key={`${card}-${i}`}
                    suit={pokerCard.suit}
                    rank={pokerCard.rank}
                    width={26}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    )
  );
};

export default TableWinnerMessage;

const containerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  zIndex: ZIndex.overlay,
  pointerEvents: 'none'
};

const bannerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.4rem',
  padding: '0.6rem 1.25rem',
  backgroundColor: 'rgba(0, 0, 0, 0.75)',
  borderRadius: '14px',
  border: '1px solid rgba(255, 215, 0, 0.3)',
  boxShadow: '0 0 20px rgba(255, 215, 0, 0.15)',
  whiteSpace: 'nowrap'
};

const headerRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem'
};

const textStyle: CSSProperties = {
  fontSize: '0.9rem',
  fontWeight: 700,
  color: '#ffffff',
  letterSpacing: '0.03em',
  fontFamily: "'Outfit', sans-serif",
  // Showdown labels are already uppercased by handLabel; this keeps the
  // all-fold banner ("Everyone folded") in its pre-EN-10766 uppercase.
  textTransform: 'uppercase'
};

const cardsRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem'
};
